import { useSyncExternalStore } from "react";
import { useSitePathname } from "@/lib/i18n/useSitePathname";
import { normalizeReferralCode } from "@/lib/referral";
import { partnerCodeFromPath } from "@/lib/usePartnerCode";

// The query string, read after hydration. useSearchParams would turn a static
// page into a client-only render up to the next Suspense boundary, and the
// pricing section and the check must stay in the server HTML.
const subscribe = (onChange: () => void) => {
  window.addEventListener("popstate", onChange);
  return () => window.removeEventListener("popstate", onChange);
};
const readSearch = () => window.location.search;
const noSearch = () => "";

/**
 * The partner code for the page being shown: from a partner page's path
 * (/p/lena) or from the ?code= (or ?ref=) a partner page passed on, e.g.
 * /eignungs-check?code=LENA or /preise?code=LENA. Still nothing is stored on
 * the device, the code travels in the URL only. Before this, the check result,
 * its WhatsApp text and the pricing cards dropped the code, so the couple paid
 * €25 more and the partner got no credit (process test 18.10.2026).
 */
export function useActivePartnerCode(): string | null {
  const pathname = useSitePathname();
  const search = useSyncExternalStore(subscribe, readSearch, noSearch);
  const fromPath = partnerCodeFromPath(pathname);
  if (fromPath) return fromPath;
  const params = new URLSearchParams(search);
  return normalizeReferralCode(params.get("code") ?? params.get("ref"));
}

/** The link with the partner code appended (?code= or &code=), or unchanged. */
export function withPartnerCode(href: string, code: string | null): string {
  if (!code) return href;
  const [path, hash] = href.split("#");
  const sep = path.includes("?") ? "&" : "?";
  return `${path}${sep}code=${encodeURIComponent(code)}${hash !== undefined ? `#${hash}` : ""}`;
}
